import {assert} from 'Asserts';
import {
  ContainerInfo,
  GridCellSummary,
  GridInfo,
  TextInfo,
  NumberInfo,
  SampleDetails,
  BooleanInfo,
} from 'constants/SampleConstants';
import {BioentityInfo} from 'folder-items/FileConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  BioentityLinkSchemaFieldTypedValue,
  BioentityTypedValue,
  BooleanSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
  GridCoordinatesTypedValue,
  IntegerSchemaFieldTypedValue,
  StorablesTypedValue,
  StringTypedValue,
  TextSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

const allowedSchemaFieldTypes = [
  FieldType.BIOENTITY_LINK,
  FieldType.STORABLE_LINK,
  FieldType.TEXT,
  FieldType.FLOAT,
  FieldType.INTEGER,
  FieldType.BOOLEAN,
];

/**
  Creates a cell that represents a sample. The cell will be created so that it matches
  the column definition of the destination column.

  Must be kept in sync with `getAcceptableSampleFiltersByColumn`, which filters destination
  columns by what type of sample they can accept.
*/
export default function makeSampleCell(
  columnDefinition: ColumnDefinition,
  sampleDetails: SampleDetails
): Cell | null {
  switch (columnDefinition.type) {
    case TableColumnType.EXISTING_BIOENTITY:
      assert(sampleDetails.bioentityInfo != null, 'Bioentity cell requires a bioentitySummary', {
        columnDefinition,
        sampleDetails,
      });
      return makeBioentitySampleCell(sampleDetails.bioentityInfo);

    case TableColumnType.TAG_VALUE:
      assert(
        allowedSchemaFieldTypes.includes(columnDefinition.schemaField.fieldType),
        'Trying to add a sample into a tagValue column other than a bioentity, storable column, text, boolean, or numbers',
        {columnDefinition}
      );
      if (columnDefinition.schemaField.fieldType === FieldType.BIOENTITY_LINK) {
        assert(sampleDetails.bioentityInfo != null, 'Bioentity tagValue cell requires a bioentitySummary', {
          columnDefinition,
          sampleDetails,
        });
        return makeBioentityLinkSampleCell(sampleDetails.bioentityInfo);
      } else if (
        columnDefinition.schemaField.fieldType === FieldType.STORABLE_LINK &&
        sampleDetails.containerInfo != null
      ) {
        return makeContainerSampleCell(sampleDetails.containerInfo);
      } else if (
        columnDefinition.schemaField.fieldType === FieldType.STORABLE_LINK &&
        sampleDetails.plateInfo != null
      ) {
        return makeGridSampleCell(sampleDetails.plateInfo);
      } else if (
        columnDefinition.schemaField.fieldType === FieldType.STORABLE_LINK &&
        sampleDetails.boxInfo != null
      ) {
        return makeGridSampleCell(sampleDetails.boxInfo);
      } else if (
        columnDefinition.schemaField.fieldType === FieldType.BOOLEAN &&
        sampleDetails.booleanInfo != null
      ) {
        return makeBooleanCell(sampleDetails.booleanInfo);
      } else if (
        columnDefinition.schemaField.fieldType === FieldType.TEXT &&
        sampleDetails.textInfo != null
      ) {
        return makeStringCell(sampleDetails.textInfo);
      } else if (sampleDetails.numberInfo != null) {
        if (columnDefinition.schemaField.fieldType === FieldType.FLOAT) {
          return makeFloatCell(sampleDetails.numberInfo);
        } else if (columnDefinition.schemaField.fieldType === FieldType.INTEGER) {
          return makeIntegerCell(sampleDetails.numberInfo);
        }
      }
      return null;
    case TableColumnType.CUSTOM:
      if (
        columnDefinition.customColumnConfig.dataType === CustomColumnDataType.TEXT &&
        sampleDetails.textInfo != null
      ) {
        return makeStringCell(sampleDetails.textInfo);
      } else if (
        columnDefinition.customColumnConfig.dataType === CustomColumnDataType.BOOLEAN &&
        sampleDetails.booleanInfo != null
      ) {
        return makeBooleanCell(sampleDetails.booleanInfo);
      } else if (sampleDetails.numberInfo != null) {
        if (columnDefinition.customColumnConfig.dataType === CustomColumnDataType.FLOAT) {
          return makeFloatCell(sampleDetails.numberInfo);
        } else if (columnDefinition.customColumnConfig.dataType === CustomColumnDataType.INTEGER) {
          return makeIntegerCell(sampleDetails.numberInfo);
        }
      }
      return null;
    case TableColumnType.CONTAINERS:
      assert(sampleDetails.containerInfo != null, 'Container cell requires a containerInfo', {
        columnDefinition,
        sampleDetails,
      });
      return makeContainerSampleCell(sampleDetails.containerInfo);

    case TableColumnType.PLATE:
      assert(sampleDetails.plateInfo != null, 'Plate cell requires a plateInfo', {
        columnDefinition,
        sampleDetails,
      });
      return makeGridSampleCell(sampleDetails.plateInfo);

    case TableColumnType.BOX:
      assert(sampleDetails.boxInfo != null, 'Box cell requires a boxInfo', {
        columnDefinition,
        sampleDetails,
      });
      return makeGridSampleCell(sampleDetails.boxInfo);

    case TableColumnType.ANTIBODY_DOMAIN:
      assert(sampleDetails.bioentityInfo != null, 'Antibody domain cell requires a bioentityInfo', {
        columnDefinition,
        sampleDetails,
      });
      return makeBioentitySampleCell(sampleDetails.bioentityInfo);

    default:
      assert(false, 'Unknown column type when adding a new sample', {
        columnDefinition,
        sampleDetails,
      });
  }
}

export function makeBioentitySampleCell(bioentityInfo: BioentityInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: BaseTypedValueType.BIOENTITY,
      bioentitySummary: {id: bioentityInfo.id, name: bioentityInfo.name},
    } satisfies BioentityTypedValue,
  };
}

function makeBioentityLinkSampleCell(bioentityInfo: BioentityInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: FieldType.BIOENTITY_LINK,
      value: null,
      targetFiles: [
        {
          id: bioentityInfo.id,
          name: bioentityInfo.name,
          url: bioentityInfo.editURL ?? '',
        },
      ],
    } satisfies BioentityLinkSchemaFieldTypedValue,
  };
}

export function makeContainerSampleCell(containerInfo: ContainerInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: BaseTypedValueType.STORABLES,
      storableSummaries: [{id: containerInfo.id, name: containerInfo.name}],
    } satisfies StorablesTypedValue,
  };
}

export function makeGridSampleCell(gridInfo: GridInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: BaseTypedValueType.STORABLES,
      storableSummaries: [{id: gridInfo.id, name: gridInfo.name}],
    } satisfies StorablesTypedValue,
  };
}

export function makePositionSampleCell(position: GridCellSummary | string): Cell {
  let typedValue: GridCoordinatesTypedValue | StringTypedValue;
  if (typeof position === 'string') {
    typedValue = {
      type: BaseTypedValueType.STRING,
      rawValue: position,
    };
  } else {
    typedValue = {
      type: BaseTypedValueType.GRID_COORDINATES,
      gridCoordinates: {
        columnIndex: position.columnIndex,
        rowIndex: position.rowIndex,
      },
    };
  }
  return {
    text: '',
    typedValue,
  };
}

export function makeStringCell(textInfo: TextInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: FieldType.TEXT,
      value: textInfo.value,
    } satisfies TextSchemaFieldTypedValue,
  };
}

export function makeFloatCell(numberInfo: NumberInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: FieldType.FLOAT,
      value: String(numberInfo.value),
      rawValue: String(numberInfo.value),
      floatValue: numberInfo.value,
    } satisfies FloatSchemaFieldTypedValue,
  };
}

export function makeIntegerCell(numberInfo: NumberInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: FieldType.INTEGER,
      value: String(numberInfo.value),
      integerValue: numberInfo.value,
    } satisfies IntegerSchemaFieldTypedValue,
  };
}

export function makeBooleanCell(booleanInfo: BooleanInfo): Cell {
  return {
    text: '',
    typedValue: {
      type: FieldType.BOOLEAN,
      value: booleanInfo.value == null ? null : booleanInfo.value ? 'True' : 'False',
      booleanValue: booleanInfo.value,
    } satisfies BooleanSchemaFieldTypedValue,
  };
}
